import { BillingPeriod, BillingType, SERVICE_TYPE } from "providers/Auth";
import { MagicLink } from "./address";
import {
  IntegrationCredential,
  PartnerFeatures,
  PropertyManagementSystem
} from "shared/types";
import { BillingConfiguration } from "./billing-configuration";

export enum FEATURES {
  FREE_CHECKOUT = "free_checkout",
  SDK = "sdk",
  DELINQUENCY_REPORTING = "delinquency_reporting",
  API_ACCESS = "api_access",
  BOOM_SCREEN = "boom_screen",
  BOOM_CRM = "boom_crm",
  PROVISIONED_PHONE_NUMBER = "provisioned_phone_number",
  CRM_FEATURE_WORKFLOWS = "crm_feature_workflows",
  WORKFLOWS = "workflows",
  TENANT_LEADS = "tenant_leads",
  TASKS = "tasks",
  LOCKBOXES = "lockboxes",
  UI_MASTER_SEARCH = "ui_master_search",
  SHOWINGS = "showings",
  MARKET_RENT = "market_rent",
  SYNDICATION = "syndication",
  CRM_LISTING_SYNC = "crm_listing_sync",
  COMMUNICATION_EMAIL = "communication_email",
  CUSTOMIZE_LEAD_SOURCES_STAGES = "customize_lead_sources_stages",
  RENT_REPORTING = "rent_reporting",
  ALLOW_MANUAL_PROPERTY_MANAGEMENT = "allow_manual_property_management",
  SITES_AND_COMMUNITIES = "sites_and_communities",
  SHOW_FULL_SSN = "show_full_ssn",
  USE_PROPERTY_NAME_IN_EMAIL = "use_property_name_in_email",
  PROPERTY_GROUP_MARKET = "property_group_market",
  USE_PROPERTY_NAME_IN_SUBMISSION_DISCLOSURE = "use_property_name_in_submission_disclosure",
  ENTERPRISE_PROPERTYWARE = "enterprise_propertyware",
  MANUAL_APPLICATION_PROPERTY_MANAGER_SUBMISSION_DISCLOSURE = "manual_application_property_manager_submission_disclosure",
  AI_APPLICATION_SUMMARY = "ai_application_summary",
  TRANSUNION_CREDIT_REPORT = "transunion_credit_report",
  PMS_LISTING_SYNC = "pms_listing_sync",
  ENABLE_CREDIT_IMPACT_DASHBOARD = "enable_credit_impact_dashboard",
  HOME_SITE_RENT = "home_site_rent",
  WEEKLY_BILLING = "weekly_billing",
  SCHEDULING = "scheduling",
  TEMPLATE_FEE_COLLECTION = "template_fee_collection",
  USE_PROPERTYWARE_MULTIMEDIA_SCRAPER = "use_propertyware_multimedia_scraper",
  WIP_FEAT_PREMIUM_RENTAL_VERIFICATION = "wip_feat_premium_rental_verification",
  COMMUNICATION = "communication",
  COMMUNICATION_SMS = "communication_sms",
  NATIVE_CHAT = "native_chat",
  COMMUNICATION_PHONE = "communication_phone",
  ENABLE_IDENTITY_THEFT_PROTECTION = "enable_identity_theft_protection",
  USE_SLIM_APPLICATIONS_API = "use_slim_applications_api",
  EXTENDED_BANK_STATEMENTS = "extended_bank_statements",
  STRIPE_MANAGED_BILLING = "stripe_managed_billing",
  MULTIPLE_PROPERTY_MANAGEMENT_SYSTEMS = "multiple_property_management_systems",
  COMBINED_SEARCH_V2 = "combined_search_v2",
  ENGLISH_ONLY = "english_only",
  WIP_BOOM_FINANCE = "wip_boom_finance",
  BOOM_SCREEN_OUTCOMES = "boom_screen_outcomes",
  CRM_AI_VOICE_AGENT = "crm_ai_voice_agent",
  CRM_AI_AUTO_RESPONSE = "crm_ai_auto_response",
  CRM_AI_SUGGESTED_REPLIES = "crm_ai_suggested_replies",
  CRM_AI_LEAD_SCORING = "crm_ai_lead_scoring",
  OWNER_LEADS = "owner_leads",
  CRM_ANALYTICS = "crm_analytics",
  ARGYLE_PAYROLL = "argyle_payroll",
  UI_BOOM_SCREEN_ANALYTICS = "ui_boom_screen_analytics",
  ONBOARDING_CHECKLIST = "onboarding_checklist",
  UI_ONBOARDING_V2 = "ui_onboarding_v2",
  /**
   * Mirrors `Boom::Partner::FEATURES`. The partner-wide entitlement / kill
   * switch for AppFolio dynamic pricing: `Partner#dynamic_pricing_active_for?`
   * requires it *and* the per-credential `appfolio_dynamic_pricing` setting
   * (admin-only) before any matrix is surfaced or applied. Matrices keep being
   * ingested either way, so a unit having a matrix no longer implies it is
   * dynamically priced. Nothing in the portal gates on this yet.
   */
  PMS_DYNAMIC_PRICING = "pms_dynamic_pricing"
}

type BankAccount = {
  id: number;
  account_number: string;
  routing_number: string;
  account_nickname: string;
  account_subtype: "checking" | "savings";
  default: boolean;
  name: string;
  last4?: string;
};

export type Partner = {
  name: string;
  url?: string;
  logo_url: string | null;
  algolia: {
    app_id?: string;
    client_search_key?: string;
    index_name?: string;
  } | null;
  billing_period: BillingPeriod;
  pending_billing_period?: BillingPeriod;
  pending_billing_period_effective_at?: string;
  billing_type?: BillingType;
  features: FEATURES[];
  /**
   * Entitlement plan; "legacy" (or absent) means ops never assigned one and
   * entitlements resolve from the raw feature array.
   */
  plan?: "legacy" | "core" | "pro" | "custom" | null;
  service_type?: SERVICE_TYPE;
  property_management_system?: {
    name: PropertyManagementSystem | "";
    last_synced_at: string | null;
  };
  integration_credentials?: IntegrationCredential[];
  magic_link?: MagicLink;
  invoice_billing_day: number;
  payout_billing_day: number;
  payout_bank_account: BankAccount;
  invoice_bank_account: BankAccount;
  opt_out_period_days: number;
  opt_in_period_days: number;
  unexpired_opt_in_period: boolean;
  notification_settings: {
    application_started: boolean;
    application_submitted: boolean;
    application_group_submitted?: boolean;
    applicant_submitted_application?: boolean;
    rental_history_verification_completed: boolean;
  };
  partner_features: PartnerFeatures[];
  can_update_billing_type?: boolean;
  listing_url: string;
  calendar_account_limit?: number | null;
  connected_calendar_account_count?: number | null;
  phone_number_limit?: number | null;
  provisioned_phone_number_count?: number | null;
  workflow_limit?: number | null;
  auto_enrollment_default?: string | null;
  auto_enrollment_enabled_date?: string | null;
  enrollment_eligibility_date?: string | null;
  hubspot_hub_id?: string;
  codebox_connected?: boolean;
  sentrilock_connected?: boolean;
  igloohome_connected?: boolean;
  zillow_connected?: boolean;
  zumper_connected?: boolean;
  dwellsy_connected?: boolean;
  colors?: Record<string, Record<string, string>>;
  apartments_com_connected?: boolean;
  mhvillage_connected?: boolean;
  boom_finance_external_id?: string | null;
  appfolio_supergood_enabled?: boolean;
  billing_configuration: BillingConfiguration | null;
  property_type?:
    | "single_family"
    | "multi_family"
    | "manufactured_housing"
    | null;
  preferred_identity_verification_vendor?: "plaid" | "persona" | null;
};
